<script lang="ts">
	// UC-4.R6 and UC-66: Pick a driver (T33), a Sheet over Sell and Orders.
	//
	// The Pick a table pattern (T15). A delivery's Send with no driver opens
	// it, and one tap on a driver picks him and finishes the Send: one tap
	// more per delivery, with none picked first, so nobody sends a delivery
	// with a driver nobody chose (decision 2026-10-28-delivery-drivers). The
	// shop's active drivers come in name order from the store, as tiles in
	// reading order.
	//
	// In change mode, from an open delivery on Orders, the title names the
	// order and its driver is marked; a tap on another driver changes it, at
	// once for a manager signed in, through Manager approval (T20) for anyone
	// else. A refusal shows in place under the list.
	//
	// The list the till has shows at once; the read that opening starts
	// replaces it. With no list yet the compass shows, and a read that failed
	// with nothing in hand says so, with Retry. A shop with no driver tells a
	// manager so and offers Drivers (T34), and tells a cashier whom to ask.

	import Button from '../components/Button.svelte';
	import Loading from '../components/Loading.svelte';
	import NameTile from '../components/NameTile.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { message, type Language } from '../i18n/messages';
	import type { Driver, DriversRead } from '../till/drivers.svelte';

	interface Props {
		open: boolean;
		language: Language;
		drivers: Driver[];
		read: DriversRead;
		/** The driver the cart or the order names now, or ''. */
		current: string;
		/** The sheet's title: "Pick a driver", or in change mode "Change driver of A-12". */
		title: string;
		/** Change mode: one open delivery's driver, from Orders (UC-66). */
		changing: boolean;
		/** A manager is signed in, who may add drivers (T34). */
		isManager: boolean;
		/** A change is on its way, and has run past 300 ms. */
		saving: boolean;
		/** Why the last change did not go through, in cashier words, or ''. */
		failure: string;
		onPick: (driver: Driver) => void;
		onRetry: () => void;
		/** A manager's way to the shop's drivers (T34). */
		onDrivers: () => void;
		onClose: () => void;
	}

	let {
		open,
		language,
		drivers,
		read,
		current,
		title,
		changing,
		isManager,
		saving,
		failure,
		onPick,
		onRetry,
		onDrivers,
		onClose,
	}: Props = $props();
</script>

<Sheet
	{open}
	{title}
	titleTestId="pick-driver-title"
	{onClose}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="pick-driver-close"
	testId="pick-driver-sheet"
	locked={saving}
>
	<div class="body" data-scroll="">
		{#if drivers.length === 0 && read === 'loading'}
			<div class="wait">
				<Loading
					size="lg"
					label={message(language, 'driversLoading')}
					caption={message(language, 'driversLoading')}
				/>
			</div>
		{:else if drivers.length === 0 && read === 'error'}
			<div class="failed" role="alert" data-testid="pick-driver-failed">
				<p class="said">{message(language, 'driversLoadFailed')}</p>
				<Button
					testId="pick-driver-retry"
					label={message(language, 'retry')}
					tone="secondary"
					size="md"
					onPress={onRetry}
				/>
			</div>
		{:else if drivers.length === 0}
			<div class="none" data-testid="pick-driver-none">
				{#if isManager}
					<p class="said">{message(language, 'driversNoneYet')}</p>
					<Button
						testId="pick-driver-manage"
						label={message(language, 'driversTitle')}
						tone="secondary"
						size="md"
						onPress={onDrivers}
					/>
				{:else}
					<p class="said">{message(language, 'driversAskSomeone')}</p>
				{/if}
			</div>
		{:else}
			<ul class="tiles" role="list" aria-label={message(language, 'driversTitle')}>
				{#each drivers as driver (driver.id)}
					<NameTile
						testId="pick-driver-{driver.id}"
						name={driver.name}
						chosen={changing ? driver.id === current : undefined}
						onPick={() => {
							if (!saving) onPick(driver);
						}}
					/>
				{/each}
			</ul>
		{/if}
		{#if failure}
			<p class="refused" role="alert" data-testid="pick-driver-refused">{failure}</p>
		{/if}
	</div>
</Sheet>

<style>
	.body {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.wait {
		flex: 1;
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.failed,
	.none {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-5);
	}

	.said {
		margin: 0;
		font-size: var(--text-base);
		font-weight: 600;
		color: var(--text-strong);
	}

	.refused {
		margin: 0;
		font-size: var(--text-sm);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--ink-flag-deep);
	}

	.tiles {
		margin: 0;
		padding: 0;
		list-style: none;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(var(--name-min), 1fr));
		gap: var(--touch-gap);
	}
</style>
